<template>
  <v-layout class="grey lighten-4" column>
    <div class="noto bold pa-3 d-md-flex align-center" width="100%">
      <v-btn
        small
        text
        rounded
        fab
        color="success"
        :to="{ name: 'Subrecipes' }">
        <v-icon>
          mdi-arrow-left
        </v-icon>
      </v-btn>
      <div class="title primary--text">{{ section }}</div>
      <v-spacer />
      <div class="d-flex justify-end align-center">
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          label="Busca recetas por nombre"
          solo
          rounded
          hide-details
          flat
          dense/>
      </div>
    </div>

    <div class="px-3">
      <v-row>
        <v-col md="4">
          <div class="d-flex justify-end align-center">
            <v-autocomplete
              prepend-inner-icon="mdi-magnify"
              solo
              rounded
              hide-details
              flat
              dense
              hide-no-data
              hide-selected
              clearable
              item-text="name"
              item-value="uuid"
              label="Buscar recetas por ingrediente"
              v-model="input"
              :items="products.data"
              :loading="products.loading"
              @input.native="searchProducts"
              return-object>
              <template v-slot:item="{ item }">
                <template>
                  <v-list-item-content>
                    <v-list-item-title>
                      {{ item.name }}
                    </v-list-item-title>
                    <v-list-item-subtitle v-if="item.subrecipe">
                      Subreceta
                    </v-list-item-subtitle>
                    <v-list-item-subtitle v-else>
                      <div>
                        {{ item.provider }}
                      </div>
                      <div>
                        {{ item.satCode }}
                      </div>
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </template>
              </template>
            </v-autocomplete>
          </div>
        </v-col>
        <v-spacer class="d-none d-md-block"/>
        <v-col>
          <div class="d-flex justify-end align-center">
            <v-btn
              :disabled="!(selected.length && input)"
              class="mr-3"
              rounded
              small
              depressed
              color="error"
              @click="openRemoveDialog">
              Eliminar ingrediente
            </v-btn>
            <v-btn
              :disabled="!(selected.length && input)"
              class="mr-3"
              rounded
              small
              depressed
              color="primary"
              @click="openReplaceDialog">
              Reemplazar ingrediente
            </v-btn>
            <v-btn
              :disabled="!selected.length"
              rounded
              small
              depressed
              color="primary"
              @click="openAddDialog">
              Agregar ingrediente
            </v-btn>
          </div>
        </v-col>
      </v-row>
    </div>
    <v-flex xs12>
      <v-data-table
        v-model="selected"
        :loading-text="table.title"
        :server-items-length="table.total"
        :loading="table.loading"
        :headers="table.headers"
        :items="recipesList.models"
        :options="table.options"
        :footer-props="{
          'items-per-page-options': [10, 20, 30, 40, 50]
        }"
        item-key="uuid"
        show-select
        sort-by="name"
        @update:options="updateTable"
        class="elevation-1 fixed-header">
        <template v-slot:item.internalId="{ item }">
          <span v-if="item.internalId">
            {{ item.internalId }}
          </span>
          <span v-else>
            -
          </span>
        </template>
        <template v-slot:item.name="{ item }">
          {{ item.product ? item.product.name : item.name }}
        </template>
        <template v-slot:item.area="{ item }">
          <span v-if="item.area">
            {{ item.area.name }}
          </span>
          <span v-else>
            -
          </span>
        </template>
        <template v-slot:item.section="{ item }">
          <span v-if="item.section">
            {{ item.section.name }}
          </span>
          <span v-else>
            -
          </span>
        </template>
        <template v-slot:item.cost="{ item }">
          <div class="text-right">
            ${{ item.cost | toFix2 | numberWithComma }}
          </div>
        </template>
        <template v-slot:item.price="{ item }">
          <div class="text-right">
            <span v-if="item.price">
              ${{ item.price | toFix2 | numberWithComma }}
            </span>
            <span v-else>
              -
            </span>
          </div>
        </template>
        <template v-slot:item.status="{ item }">
          <template>
            <div :class="`${statusColors[item.status]}--text`">
              {{ convertStatus(item.status)['text'] }}
            </div>
          </template>
        </template>
      </v-data-table>
    </v-flex>

    <v-dialog
      @keydown.esc="addDialog=false"
      max-width="600"
      v-model="addDialog">
      <v-card>
        <v-card-title>
          <span class="headline">Agregar ingrediente a recetas</span>
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col>
              <v-autocomplete
                label="Nuevo ingrediente"
                outlined
                solo
                rounded
                hide-details
                hide-no-data
                flat
                dense
                :items="ingredients.data"
                :loading="ingredients.loading"
                item-text="name"
                item-value="id"
                v-model="add.ingredient"
                @change="ingredientSelected"
                @input.native="searchIngredient"
                return-object />
            </v-col>
            <v-col>
              <v-select
                label="Proveedor"
                outlined
                solo
                rounded
                hide-details
                flat
                dense
                :disabled="providers.disabled"
                :items="providers.data"
                item-value="uuid"
                item-text="name"
                v-model="add.provider"
                @change="providerSelected"
                return-object>
                <template v-slot:item="{ item }">
                  <div style="width: 100%">
                    <div>
                      {{ item.name }}
                    </div>
                    <div class="d-flex justify-space-between">
                      <div class="body-2">
                        {{ item.display }}
                        <span v-if="item.brand">
                          | {{ item.brand }}
                        </span>
                      </div>
                    </div>
                    <div>
                      <div class="body-2">
                        ${{ item.price | toFixNumber(2) | numberWithComma }} x {{ item.unit }}
                      </div>
                    </div>
                  </div>
                </template>
              </v-select>
            </v-col>
          </v-row>
          <v-divider />
          <v-list>
            <v-list-item
              v-for="item in add.recipes"
              :key="item.recipe">
              <div class="d-flex">
                <div class="mr-3" style="width: 100%">
                  {{ item.name }}
                </div>
                <div class="mr-3">
                  <v-text-field
                    v-model.number="item.quantity"
                    label="Cantidad"
                    outlined
                    solo
                    rounded
                    hide-details
                    flat
                    dense/>
                </div>
                <div>
                  <v-select
                    v-model="item.unit"
                    label="Unidad"
                    outlined
                    solo
                    rounded
                    hide-details
                    flat
                    dense
                    :items="units.selectable"
                    item-text="name"
                    item-value="uuid"/>
                </div>
              </div>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="error" class="mr-4" @click="closeAddDialog">{{ $t('recipes.close') }}</v-btn>
          <v-btn color="success" class="mr-4" @click="addIngredients">{{ $t('recipes.save') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      @keydown.esc="removeDialog=false"
      max-width="400"
      v-model="removeDialog">
      <v-card>
        <v-card-title>
          <span class="headline">¿Deseas eliminar el ingrediente?</span>
        </v-card-title>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="error" class="mr-4" @click="closeRemoveDialog">{{ $t('recipes.close') }}</v-btn>
          <v-btn color="success" class="mr-4" @click="removeIngredients">{{ $t('recipes.save') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      @keydown.esc="replaceDialog=false"
      max-width="600"
      v-model="replaceDialog">
      <v-card>
        <v-card-title>
          <span class="headline">Reemplazar ingrediente a recetas</span>
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col>
              <v-autocomplete
                label="Nuevo ingrediente"
                outlined
                solo
                rounded
                hide-details
                hide-no-data
                flat
                dense
                :items="ingredients.data"
                :loading="ingredients.loading"
                item-text="name"
                item-value="id"
                v-model="replace.ingredient"
                @change="ingredientSelected"
                @input.native="searchIngredient"
                return-object/>
            </v-col>
            <v-col>
              <v-select
                label="Proveedor"
                outlined
                solo
                rounded
                hide-details
                flat
                dense
                :disabled="providers.disabled"
                :items="providers.data"
                item-value="uuid"
                item-text="name"
                v-model="replace.provider"
                @change="providerSelected"
                return-object>
                <template v-slot:item="{ item }">
                  <div style="width: 100%">
                    <div>
                      {{ item.name }}
                    </div>
                    <div class="d-flex justify-space-between">
                      <div class="body-2">
                        {{ item.display }}
                        <span v-if="item.brand">
                          | {{ item.brand }}
                        </span>
                      </div>
                    </div>
                    <div>
                      <div class="body-2">
                        ${{ item.price | toFixNumber(2) | numberWithComma }} x {{ item.unit }}
                      </div>
                    </div>
                  </div>
                </template>
              </v-select>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="error" class="mr-4" @click="closeReplaceDialog">{{ $t('recipes.close') }}</v-btn>
          <v-btn color="success" class="mr-4" @click="replaceIngredients">{{ $t('recipes.save') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-overlay :value="overlay" z-index="99999">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </v-layout>
</template>

<script>
import Recipe from '@/models/Recipe'
import RecipesList from '@/models/collections/RecipesList'
import { debounce, flatten } from 'lodash'

export default {
  name: 'EditRecipes',
  data () {
    let recipesList = new RecipesList()
    let recipe = new Recipe({}, recipesList)

    return {
      recipeModel: recipe,
      recipesList,
      overlay: false,
      addDialog: false,
      replaceDialog: false,
      removeDialog: false,
      section: 'Editar múltiples subrecetas',
      table: {
        title: this.$i18n.t('recipes.loading'),
        loading: true,
        headers: [
          { text: this.$i18n.t('recipes.headers.name'), value: 'name' },
          { text: this.$i18n.t('recipes.headers.status'), value: 'status', sortable: false }
        ],
        data: [],
        total: 0,
        options: {
          itemsPerPage: 50,
          page: 1
        }
      },
      status: [
        { text: 'En elaboración', value: 'in-progress' },
        { text: 'En menú', value: 'attached' },
        { text: 'No adjuntado', value: 'unattached' },
        { text: 'Archivado', value: 'archived' }
      ],
      statusColors: {
        'in-progress': 'grey',
        'attached': 'primary',
        'unattached': 'danger',
        'archived': 'bluegrey'
      },
      search: '',
      selected: [],
      products: {
        loading: false,
        data: [],
        total: 0
      },
      add: {
        ingredient: null,
        provider: null,
        recipes: []
      },
      replace: {
        ingredient: null,
        provider: null,
        recipes: []
      },
      ingredients: {
        loading: false,
        data: [],
        total: 0
      },
      providers: {
        disabled: false,
        data: [],
        total: 0
      },
      units: {
        disabled: false,
        data: [],
        selectable: [],
        total: 0
      },
      input: null
    }
  },
  mounted () {
    this.units.data = this.$store.state.login.units.data
  },
  watch: {
    'search': debounce(async function (val) {
      try {
        await this.load({
          restaurant: this.$store.state.currentRestaurant.data._id,
          area: this.$store.state.area.id,
          skip: 0,
          sort: 'name',
          name: `/${val}/i`,
          status: 'attached',
          subrecipe: true
        })
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
    }, 300),
    input (val) {
      this.inputSelected(val)
    }
  },
  methods: {
    openAddDialog () {
      this.addDialog = true
      this.add.recipes = this.selected.map(x => ({
        name: x.$.name,
        recipe: x.$.uuid,
        quantity: null,
        unit: null
      }))
    },
    closeAddDialog () {
      this.addDialog = false
      this.add = {
        ingredient: null,
        provider: null,
        recipes: []
      }
    },
    openReplaceDialog () {
      this.replaceDialog = true
      this.replace.recipes = this.selected.map(x => ({
        name: x.$.name,
        recipe: x.$.uuid
      }))
    },
    closeReplaceDialog () {
      this.replaceDialog = false
      this.replace = {
        ingredient: null,
        provider: null,
        recipes: []
      }
    },
    openRemoveDialog () {
      this.removeDialog = true
    },
    closeRemoveDialog () {
      this.removeDialog = false
    },
    searchIngredient: debounce(async function (event) {
      this.ingredients.loading = true
      try {
        if (event.target.value.length > 2) {
          const params = { name: `/${event.target.value}/i`, skip: 0, global: false, limit: 20 }
          const recipeParams = Object
            .assign({ subrecipe: true, status: 'attached', area: this.$store.state.area.id }, params)
          const [products, subrecipes] = await Promise.all([
            this.$api.get('/v1/products', params),
            this.$api.get('/v1/recipes', recipeParams)
          ])
          this.ingredients.data = products.data
            .map(product => {
              if (product.restaurant) {
                product.name = `${product.name} | Sugerido`
              }
              return product
            })
            .concat(subrecipes.data)
          this.ingredients.total = products.total + subrecipes.total
        }
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
      this.ingredients.loading = false
    }, 600),
    ingredientSelected: async function (product) {
      this.providers.data = []
      this.providers.total = 0
      this.providers.disabled = true
      if (!product.subrecipe) {
        const response = await this.$api.get(`/v1/products/${product.uuid}`)
        this.providers.data = response.choices.map(x => {
          let display = `${x.display.name}`
          if (x.display.code !== x.unit.code) {
            display = display.concat(` de ${x.contentQuantity} ${x.unit.code}`)
          }
          return {
            name: x.provider.name,
            brand: x.brand,
            uuid: x.uuid,
            price: x.amount / x.contentQuantity,
            unit: x.unit.name,
            magnitude: x.unit.magnitude,
            display
          }
        })
        this.providers.total = response.choices.length
        this.providers.disabled = false
      } else {
        this.units.selectable = this.units.data.filter(x => x.magnitude === product.unitStock.magnitude)
        this.providers.disabled = true
      }
    },
    providerSelected (choice) {
      if (choice) {
        this.units.selectable = this.units.data.filter(x => x.magnitude === choice.magnitude)
      }
    },
    searchProducts: debounce(async function (event) {
      this.products.loading = true
      try {
        const params = { name: `/${event.target.value}/i`, skip: 0, listAll: true, limit: 20 }
        const recipeParams = Object
          .assign({ subrecipe: true, status: 'attached', area: this.$store.state.area.id }, params)
        const [products, subrecipes] = await Promise.all([
          this.$api.get('/v1/products', params),
          this.$api.get('/v1/recipes', recipeParams)
        ])

        const choices = await Promise.all(products.data.map(async (product) => {
          if (product.restaurant) {
            product.name = `${product.name} | Sugerido`
          }

          const res = await this.$api.get(`/v1/products/${product.uuid}`)

          return res.choices.map(choice => ({
            name: product.name,
            satCode: choice.satCode,
            product: product.uuid,
            provider: choice.provider.name,
            uuid: choice.uuid
          }))
        }))

        this.products.data = flatten(choices).concat(subrecipes.data)
        this.products.total = products.total + subrecipes.total
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
      this.products.loading = false
    }, 600),
    inputSelected: async function (input) {
      this.selected = []
      const params = {
        restaurant: this.$store.state.currentRestaurant.data._id,
        limit: this.table.options.itemsPerPage,
        skip: (this.table.options.itemsPerPage * (this.table.page - 1)),
        sort: 'name',
        area: this.$store.state.area.id,
        subrecipe: true
      }
      if (input) {
        if (input.subrecipe) {
          params.product = input.uuid
        } else {
          params.choice = input.uuid
        }
      }
      this.load(params)
    },
    convertStatus (key) {
      return this.status.find(stat => stat.value === key) || ''
    },
    updateTable: async function (data) {
      const { sortBy, sortDesc } = data
      if (this.$store.state.area) {
        await this.load({
          limit: data.itemsPerPage,
          skip: (data.itemsPerPage * (data.page - 1)),
          sort: `${sortDesc[0] ? '-' : ''}${sortBy[0]}`,
          restaurant: this.$store.state.currentRestaurant.data._id,
          area: this.$store.state.area.id,
          subrecipe: true
        })
      }
    },
    async load (params = null) {
      params = params || {
        restaurant: this.$store.state.currentRestaurant.data._id,
        limit: this.table.options.itemsPerPage,
        skip: (this.table.options.itemsPerPage * (this.table.page - 1)),
        sort: 'name',
        area: this.$store.state.area.id,
        subrecipe: true
      }
      this.table.loading = true
      try {
        const { response } = await this.recipeModel.fetch({ params })
        this.recipesList.replace([response.data.data])
        this.table.total = response.data.total
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
      this.table.loading = false
    },
    async removeIngredients () {
      this.overlay = true
      try {
        const recipes = this.selected.map(x => x.uuid)
        const uuid = this.input.subrecipe ? this.input.uuid : this.input.product
        await this.$api.del(`/v1/recipes/mass/remove-ingredient/${uuid}`, { recipes })
        await this.load()
        this.$toasted.show('Los ingredientes se eliminaron correctamente', { duration: 5000, type: 'success' })
        this.selected = []
        this.input = null
      } catch (error) {
        this.$toasted.show(error.message, { duration: 5000 })
      }
      this.overlay = false
    },
    async addIngredients () {
      this.overlay = true
      try {
        const ingredient = this.add.ingredient.subrecipe ? this.add.ingredient.uuid : this.add.provider.uuid
        const payload = {
          subrecipe: !!this.add.ingredient.subrecipe,
          recipes: this.add.recipes.map(x => ({
            recipe: x.recipe,
            quantity: x.quantity,
            unit: x.unit
          }))
        }
        await this.$api.post(`/v1/recipes/mass/add-ingredient/${ingredient}`, payload)
        await this.load()
        this.$toasted.show('Los ingredientes se reemplazaron correctamente', { duration: 5000, type: 'success' })
        this.closeAddDialog()
        this.selected = []
        this.input = null
      } catch (error) {
        this.$toasted.show(error.message, { duration: 5000 })
      }
      this.overlay = false
    },
    async replaceIngredients () {
      this.overlay = true
      try {
        const productId = this.input.uuid
        const payload = {
          subrecipe: this.input.subrecipe,
          newIngredient: {
            id: this.replace.ingredient.subrecipe ? this.replace.ingredient.uuid : this.replace.provider.uuid,
            subrecipe: !!this.replace.ingredient.subrecipe
          },
          recipes: this.replace.recipes.map(x => x.recipe)
        }
        await this.$api.put(`/v1/recipes/mass/replace-ingredient/${productId}`, payload)
        await this.load()
        this.$toasted.show('Los ingredientes se eliminaron correctamente', { duration: 5000, type: 'success' })
        this.closeReplaceDialog()
        this.selected = []
        this.input = null
      } catch (error) {
        this.$toasted.show(error.message, { duration: 5000 })
      }
      this.overlay = false
    },
    getSubrecipeColor (isSubrecipe) {
      return isSubrecipe ? 'teal darken-4' : 'teal lighten-5'
    },
    getSubrecipeTextColor (isSubrecipe) {
      return isSubrecipe ? 'teal lighten-5' : 'teal darken-4'
    }
  }
}
</script>
